import { useQueryClient } from '@tanstack/react-query';
import {
  type AppEnvironment,
  type AuthConfigResponse,
  type CurrentUserResponse,
  hasPermissions,
  type Permission,
} from '@k-management/shared';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApiError, apiRequest, setTokenProvider } from '../lib/api.js';
import { createSession, type SignedInIdentity, type SignInSession } from './session.js';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | {
      /** Signed in, but K-Management does not let the account in (see `code`). */
      status: 'refused';
      code: 'not-registered' | 'email-not-verified' | 'microsoft-required';
      email: string;
      message: string;
    }
  | { status: 'signed-in'; user: CurrentUserResponse }
  | { status: 'failed'; message: string };

interface AuthContextValue {
  state: AuthState;
  /** Sign-in configuration, null until loaded. */
  config: AuthConfigResponse | null;
  environment: AppEnvironment | null;
  session: SignInSession | null;
  /** Checks the signed-in account again, e.g. after the e-mail has been verified. */
  recheck: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const REFUSAL_CODES = ['not-registered', 'email-not-verified', 'microsoft-required'] as const;

/**
 * Signs the user in and resolves who they are for K-Management: the sign-in
 * proves the identity, GET /api/me tells whether the account is a registered
 * user and what they may do (docs/adr/ADR-004, ADR-005).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [session, setSession] = useState<SignInSession | null>(null);
  const identityRef = useRef<SignedInIdentity | null>(null);
  const queryClient = useQueryClient();

  const resolve = useCallback(
    async (activeSession: SignInSession, identity: SignedInIdentity | null) => {
      identityRef.current = identity;
      queryClient.clear();
      if (!identity) {
        setState({ status: 'signed-out' });
        return;
      }
      setState({ status: 'loading' });
      try {
        setState({ status: 'signed-in', user: await apiRequest<CurrentUserResponse>('/me') });
      } catch (error) {
        const code = error instanceof ApiError ? error.code : undefined;
        const refusal = REFUSAL_CODES.find((item) => item === code);
        if (refusal && error instanceof ApiError) {
          setState({
            status: 'refused',
            code: refusal,
            email: identity.email,
            message: error.message,
          });
        } else if (error instanceof ApiError && error.status === 401) {
          await activeSession.signOut();
        } else {
          setState({ status: 'failed', message: errorMessage(error) });
        }
      }
    },
    [queryClient],
  );

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      const config = await apiRequest<AuthConfigResponse>('/auth/config');
      const created = await createSession(config);
      if (cancelled) return;
      setTokenProvider(() => created.getToken());
      setSession(created);
      unsubscribe = created.subscribe((identity) => void resolve(created, identity));
    })().catch((error: unknown) => {
      if (!cancelled) setState({ status: 'failed', message: errorMessage(error) });
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [resolve]);

  const recheck = useCallback(async () => {
    if (!session) return;
    await session.refresh();
    await resolve(session, identityRef.current);
  }, [session, resolve]);

  const signOut = useCallback(async () => {
    await session?.signOut();
  }, [session]);

  const value = useMemo(
    () => ({
      state,
      config: session?.config ?? null,
      environment: session?.config.environment ?? null,
      session,
      recheck,
      signOut,
    }),
    [state, session, recheck, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}

/** The signed-in user; only for pages rendered after sign-in. */
export function useCurrentUser(): CurrentUserResponse {
  const { state } = useAuth();
  if (state.status !== 'signed-in') throw new Error('useCurrentUser requires a signed-in user');
  return state.user;
}

/** Whether the signed-in user holds every given permission (the API checks them again). */
export function useCan(...permissions: Permission[]): boolean {
  return hasPermissions(useCurrentUser().permissions, permissions);
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
